import React, {
  useCallback,
  useEffect,
  useState,
} from "react";
import {
  AlertCircle,
  Copy,
  Edit,
  ExternalLink,
  Loader2,
  Search,
} from "lucide-react";
import toast from "react-hot-toast";

import { JobService } from "@/services/jobService";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/Accordion";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import { copyToClipboard } from "@/utils/clipboard";
import { formatDate } from "@/utils/dateUtils";
import useLiveResource from "@/hooks/useLiveResource";
import { useModuleAccess } from "@/hooks/useModuleAccess";
import ModuleUnavailableNotice from "@/permissions/ModuleUnavailableNotice";
import { MODULES } from "@/permissions/modules";
import { PERMISSIONS } from "@/permissions/constant";
import {
  getUserPermissions,
  hasPermission,
} from "@/permissions/rbac";
import { useAuth } from "@/hooks/useAuth";
import { Textarea } from "@/components/ui/Textarea";

const VARIANTS = [
  { key: "strict", label: "Strict", hint: "Narrows tightly to the closest match — fewer, more precise results." },
  { key: "moderate", label: "Moderate", hint: "A balance between precision and reach." },
  { key: "broad", label: "Broad", hint: "Widens the search for maximum reach — more results, less precise." },
];

// Explicit button styles for this tab.
// ButtonShadcn variants bring their own foreground/radius classes, so the
// important modifiers here make the Boolean Search actions consistent in
// both light and dark themes and keep every action pill-shaped.
const PRIMARY_PILL_BUTTON =
  "flex shrink-0 items-center gap-1.5 !rounded-full !border !border-border-primary !bg-button-secondary px-4 py-1.5 text-[12px] font-medium !text-text-base hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50";

const ACTION_PILL_BUTTON =
  "h-7 !rounded-full !border !border-border-primary !bg-transparent px-3 text-[11px] font-medium !text-text-primary hover:!bg-background-secondary hover:!text-text-primary";

const PRIMARY_SMALL_PILL_BUTTON =
  "h-7 !rounded-full !border !border-border-primary !bg-button-secondary px-3 text-[11px] font-medium !text-text-base hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50";

/**
 * Some platforms expose a stable search URL that accepts the query directly.
 *
 * Others only expose their recruiter/CV search UI. For those platforms we
 * open the correct external search page and copy the Boolean string so the
 * recruiter can paste it immediately.
 *
 * Add new platform mappings here rather than adding routing logic inside the
 * rendered cards.
 */
const PLATFORM_SEARCH_CONFIG = [
  {
    matches: ["linkedin"],
    name: "LinkedIn",
    buildUrl: (query) =>
      `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(
        query,
      )}`,
  },
  {
    matches: ["google"],
    name: "Google",
    buildUrl: (query) =>
      `https://www.google.com/search?q=${encodeURIComponent(query)}`,
  },
  {
    matches: ["github"],
    name: "GitHub",
    buildUrl: (query) =>
      `https://github.com/search?q=${encodeURIComponent(query)}&type=users`,
  },
  {
    matches: ["bing"],
    name: "Bing",
    buildUrl: (query) =>
      `https://www.bing.com/search?q=${encodeURIComponent(query)}`,
  },

  // These recruiter products do not expose a documented/stable URL parameter
  // for pre-populating their Boolean search. Open the correct page and copy
  // the query instead of inventing a brittle query parameter.
  {
    matches: ["reed"],
    name: "Reed",
    url: "https://www.reed.co.uk/recruiter/cv-search",
  },
  {
    matches: ["indeed"],
    name: "Indeed",
    url: "https://resumes.indeed.com/",
  },
  {
    matches: ["totaljobs", "totaljob"],
    name: "Totaljobs",
    url: "https://www.totaljobs.com/recruiters/",
  },
  {
    matches: ["cvlibrary"],
    name: "CV-Library",
    url: "https://www.cv-library.co.uk/",
  },
];

const normalizePlatform = (value) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const getPlatformSearchConfig = (
  platformValue,
  platformLabel,
) => {
  const normalized = normalizePlatform(
    `${platformValue} ${platformLabel}`,
  );

  return PLATFORM_SEARCH_CONFIG.find((config) =>
    config.matches.some((match) =>
      normalized.includes(
        normalizePlatform(match),
      ),
    ),
  );
};

// `strings` (platform -> {status, strict, moderate, broad, generated_at}) is
// the single source of truth for every server-generated bit of UI below.
//
// `localEdits` is deliberately separate. Editing is frontend-only, so a
// silent refetch or WebSocket update must not accidentally persist an edit
// to the backend or destroy a recruiter-created temporary search string.
const BooleanStringsTab = ({ jobId }) => {
  // Entitlement is handled by the <ModuleGate> at the render site, which
  // renders nothing when the tenant is not entitled — so this component never
  // mounts, and `fetchData` below never runs. That is the point: an early
  // return inside here would still have fired the effect and earned a 403.
  //
  // Availability is different and stays here: strings already generated must
  // keep rendering while only Generate is disabled.
  const { user } = useAuth();

  const canGenerate = hasPermission(
    PERMISSIONS.JOB_UPDATE,
    getUserPermissions(user),
  );

  const { isAvailable: moduleAvailable } =
    useModuleAccess(MODULES.AI_BOOLEAN_SEARCH);

  const [enabled, setEnabled] = useState([]);
  const [platformLabels, setPlatformLabels] =
    useState({});
  const [strings, setStrings] = useState({});

  /**
   * Temporary frontend-only overrides:
   *
   * {
   *   linkedin: {
   *     strict: "...edited string..."
   *   }
   * }
   *
   * They are intentionally never sent to JobService.
   */
  const [localEdits, setLocalEdits] =
    useState({});

  /**
   * Only one exact platform + variant is edited at a time.
   *
   * {
   *   platform: "linkedin",
   *   variant: "strict"
   * }
   */
  const [editing, setEditing] =
    useState(null);

  const [draftValue, setDraftValue] =
    useState("");

  const [loading, setLoading] =
    useState(true);

  const [submitting, setSubmitting] =
    useState({});

  const [
    submittingAll,
    setSubmittingAll,
  ] = useState(false);

  const fetchData = useCallback(
    async ({ silent = false } = {}) => {
      if (!jobId) return;

      if (!silent) {
        setLoading(true);
      }

      try {
        const res =
          await JobService.fetchBooleanStrings(
            jobId,
          );

        setEnabled(res?.enabled ?? []);
        setPlatformLabels(
          res?.platform_labels ?? {},
        );
        setStrings(res?.strings ?? {});
      } catch (err) {
        console.error(
          "Failed to fetch boolean strings:",
          err,
        );
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [jobId],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /**
   * Temporary edits belong to one job only.
   *
   * If this component is reused while the route changes to another job,
   * discard the local-only edits rather than carrying them into another job.
   */
  useEffect(() => {
    setLocalEdits({});
    setEditing(null);
    setDraftValue("");
  }, [jobId]);

  // Every boolean-string event is a refetch. The payload used to carry the
  // generated strings themselves, per platform; the server now sends one event
  // for the job (`boolean_search/service.py:162` says why), so the per-platform
  // breakdown has no source but this read.
  //
  // The 10-second poll that used to run while a platform was processing is gone
  // with it: a frame lost while the socket was down is covered by the refetch
  // on reconnect.
  useLiveResource({
    keys: `boolean_string:${jobId}`,
    enabled: Boolean(jobId),
    onChange: () => fetchData({ silent: true }),
  });

  // A platform can only be (re-)submitted if it was never generated, or its
  // last attempt failed — once "ready" it's a locked, final state (no
  // regenerate), and a genuinely in-flight "processing" one can't be
  // re-triggered without double-running the agent. Mirrors the backend's
  // own eligibility check, which is the actual source of truth.
  const isEligible = (platform) => {
    const status =
      strings[platform]?.status;

    return (
      status !== "ready" &&
      status !== "processing"
    );
  };

  const markSubmitting = (
    platforms,
    value,
  ) => {
    setSubmitting((prev) => ({
      ...prev,
      ...Object.fromEntries(
        platforms.map((platform) => [
          platform,
          value,
        ]),
      ),
    }));
  };

  const markOptimisticProcessing = (
    platforms,
  ) => {
    setStrings((prev) => {
      const next = { ...prev };

      platforms.forEach((platform) => {
        next[platform] = {
          ...(next[platform] || {}),
          status: "processing",
        };
      });

      return next;
    });
  };

  const runGenerate = async (
    platforms,
  ) => {
    if (
      !canGenerate ||
      !platforms.length
    ) {
      return;
    }

    markSubmitting(platforms, true);
    markOptimisticProcessing(
      platforms,
    );

    try {
      await JobService.generateBooleanStrings(
        jobId,
        platforms,
      );
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
        "Failed to start generation",
      );
    } finally {
      markSubmitting(
        platforms,
        false,
      );

      // Reconcile with server truth — covers the backend accepting fewer
      // platforms than requested (already ready/in-flight elsewhere) so the
      // UI never shows an optimistic state the server didn't actually accept.
      fetchData({
        silent: true,
      });
    }
  };

  const handleGenerate = (platform) =>
    runGenerate([platform]);

  const handleGenerateAll =
    async () => {
      const targets =
        enabled.filter(isEligible);

      if (
        !canGenerate ||
        !targets.length
      ) {
        return;
      }

      setSubmittingAll(true);

      try {
        await runGenerate(targets);
      } finally {
        setSubmittingAll(false);
      }
    };

  /**
   * Returns the temporary frontend edit when one exists; otherwise returns
   * the original server-generated string.
   */
  const getDisplayString = (
    platformValue,
    variantKey,
    result,
  ) =>
    localEdits?.[platformValue]?.[
    variantKey
    ] ??
    result?.[variantKey] ??
    "";

  const handleCopy = async (text) => {
    const value = String(
      text ?? "",
    ).trim();

    if (!value) {
      toast.error(
        "There is no Boolean string to copy.",
      );
      return;
    }

    const ok =
      await copyToClipboard(value);

    if (ok) {
      toast.success(
        "Boolean string copied",
      );
    } else {
      toast.error(
        "Could not copy — copy manually",
      );
    }
  };

  const startEdit = (
    platformValue,
    variantKey,
    currentValue,
  ) => {
    setEditing({
      platform: platformValue,
      variant: variantKey,
    });

    setDraftValue(
      currentValue ?? "",
    );
  };

  const cancelEdit = () => {
    setEditing(null);
    setDraftValue("");
  };

  /**
   * Save means "use this edited value in the current frontend session".
   *
   * This feature is intentionally frontend-only. No API request is made and
   * the generated backend value remains unchanged.
   */
  const saveEdit = (
    platformValue,
    variantKey,
  ) => {
    const nextValue =
      draftValue.trim();

    if (!nextValue) {
      toast.error(
        "Boolean string cannot be empty.",
      );
      return;
    }

    setLocalEdits((prev) => ({
      ...prev,
      [platformValue]: {
        ...(prev[platformValue] ??
          {}),
        [variantKey]:
          nextValue,
      },
    }));

    setEditing(null);
    setDraftValue("");

    toast.success(
      "String updated for this session",
    );
  };

  /**
   * Opens a generated Boolean string on its target platform.
   *
   * Platforms with a stable query URL receive the string directly.
   * Platforms without one are opened normally and the Boolean string is
   * copied automatically so it can be pasted into their recruiter search.
   */
  const handleOpenPlatform =
    async (
      platformValue,
      platformLabel,
      stringValue,
    ) => {
      const query = String(
        stringValue ?? "",
      ).trim();

      if (!query) {
        toast.error(
          "There is no Boolean string to search.",
        );
        return;
      }

      const config =
        getPlatformSearchConfig(
          platformValue,
          platformLabel,
        );

      if (!config) {
        const copied =
          await copyToClipboard(query);

        if (copied) {
          toast.success(
            `${platformLabel} search URL is not configured. String copied instead.`,
          );
        } else {
          toast.error(
            `No search URL is configured for ${platformLabel}.`,
          );
        }

        return;
      }

      /*
       * Direct-query platforms can receive the Boolean string in their URL.
       */
      if (config.buildUrl) {
        const url =
          config.buildUrl(query);

        window.open(
          url,
          "_blank",
          "noopener,noreferrer",
        );

        return;
      }

      /*
       * Open the external page immediately while this handler still belongs
       * to the user's click. Waiting for clipboard work first can cause some
       * browsers to treat the later window.open() as an unsolicited popup.
       */
      window.open(
        config.url,
        "_blank",
        "noopener,noreferrer",
      );

      const copied =
        await copyToClipboard(query);

      if (copied) {
        toast.success(
          `Boolean string copied — paste it into ${config.name} search.`,
        );
      } else {
        toast.error(
          `${config.name} opened, but the string could not be copied.`,
        );
      }
    };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-text-primary" />
      </div>
    );
  }

  if (!enabled.length) {
    return (
      <div className="text-center py-12">
        <Search className="mx-auto h-8 w-8 text-text-muted mb-3" />

        <p className="text-sm text-text-muted">
          Enable Boolean search platforms in
          this job&apos;s settings to generate
          strings here.
        </p>
      </div>
    );
  }

  const eligibleForAll =
    enabled.filter(isEligible);

  const generateAllDisabled =
    submittingAll ||
    eligibleForAll.length === 0 ||
    !moduleAvailable ||
    !canGenerate;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-[16px] font-semibold text-text-primary">
            Boolean Search Strings
          </h3>

          <p className="text-sm text-text-muted">
            Generate ready-to-paste Boolean
            strings for each enabled platform.
          </p>

          {!canGenerate && (
            <p className="text-sm text-text-muted">
              You have read-only access. Job
              update permission is required to
              generate or retry strings.
            </p>
          )}
        </div>

        <Button
          type="button"
          onClick={handleGenerateAll}
          disabled={
            generateAllDisabled
          }
          className={PRIMARY_PILL_BUTTON}
        >
          {submittingAll ? (
            <Loader2
              size={14}
              className="animate-spin"
            />
          ) : null}

          Generate All
        </Button>
      </div>

      {/* The one place this panel explains the kill switch. The render site in
          job-detail passes `variant="passthrough"` precisely so this is the
          only copy about it on screen. */}
      <ModuleUnavailableNotice module={MODULES.AI_BOOLEAN_SEARCH} suffix="Strings already generated are shown below." />

      <Accordion>
        {enabled.map(
          (platformValue) => {
            const result =
              strings[platformValue] || {};

            const platformLabel =
              platformLabels[
              platformValue
              ] ?? platformValue;

            const isProcessing =
              result.status ===
              "processing";

            const isFailed =
              result.status ===
              "failed";

            const isReady =
              result.status === "ready";

            const isSubmitting =
              !!submitting[
              platformValue
              ];

            return (
              <AccordionItem
                key={platformValue}
                value={platformValue}
              >
                <AccordionTrigger>
                  <div className="flex flex-1 items-center justify-between pr-3">
                    <span className="text-[12px] text-text-primary">
                      {platformLabel}
                    </span>

                    {isProcessing ? (
                      <span className="flex items-center gap-1.5 text-[12px] text-text-muted">
                        <Loader2
                          size={14}
                          className="animate-spin"
                        />
                        Generating…
                      </span>
                    ) : isReady ? (
                      <span className="text-[12px] text-green-500">
                        Generated
                      </span>
                    ) : (
                      <span className="text-[12px] text-text-muted">
                        {isFailed
                          ? "Failed"
                          : "Not generated"}
                      </span>
                    )}
                  </div>
                </AccordionTrigger>

                <AccordionContent>
                  {isProcessing && (
                    <p className="text-sm text-text-muted">
                      Generating…
                    </p>
                  )}

                  {isFailed &&
                    !isProcessing && (
                      <div className="space-y-3">
                        <p className="text-sm text-red-400 flex items-center gap-1.5">
                          <AlertCircle
                            size={14}
                          />
                          Generation failed.
                          Try again.
                        </p>

                        <Button
                          type="button"
                          onClick={() =>
                            handleGenerate(
                              platformValue,
                            )
                          }
                          disabled={
                            isSubmitting ||
                            !moduleAvailable ||
                            !canGenerate
                          }
                          className={PRIMARY_PILL_BUTTON}
                        >
                          {isSubmitting ? (
                            <Loader2
                              size={
                                14
                              }
                              className="animate-spin"
                            />
                          ) : null}

                          Retry
                        </Button>
                      </div>
                    )}

                  {isReady && (
                    <div className="space-y-3">
                      {result.generated_at && (
                        <p className="text-xs text-text-muted">
                          Generated{" "}
                          {formatDate(
                            result.generated_at,
                          )}
                        </p>
                      )}

                      {VARIANTS.map(
                        ({
                          key,
                          label,
                          hint,
                        }) => {
                          const displayValue =
                            getDisplayString(
                              platformValue,
                              key,
                              result,
                            );

                          if (
                            !displayValue
                          ) {
                            return null;
                          }

                          const isEditing =
                            editing?.platform ===
                            platformValue &&
                            editing?.variant ===
                            key;

                          const hasLocalEdit =
                            localEdits?.[
                            platformValue
                            ]?.[key] != null;

                          return (
                            <div
                              key={key}
                              className="bg-background-primary/30 rounded-lg p-3 border border-border-primary/50"
                            >
                              <div className="flex items-center justify-between gap-3 mb-2">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-medium text-text-secondary uppercase tracking-wide" title={hint}>
                                    {
                                      label
                                    }
                                  </span>

                                  {hasLocalEdit && (
                                    <span className="rounded-full bg-tone-blue/10 px-2 py-0.5 text-[10px] font-medium text-text-blue">
                                      Edited
                                      locally
                                    </span>
                                  )}
                                </div>

                                {isEditing ? (
                                  <div className="flex items-center gap-1">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={
                                        cancelEdit
                                      }
                                      className={ACTION_PILL_BUTTON}
                                    >
                                      Cancel
                                    </Button>

                                    <Button
                                      type="button"
                                      variant="default"
                                      size="sm"
                                      onClick={() =>
                                        saveEdit(
                                          platformValue,
                                          key,
                                        )
                                      }
                                      disabled={
                                        !draftValue.trim()
                                      }
                                      className={PRIMARY_SMALL_PILL_BUTTON}
                                    >
                                      Save
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="flex flex-wrap items-center justify-end gap-1">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() =>
                                        handleCopy(
                                          displayValue,
                                        )
                                      }
                                      className={`${ACTION_PILL_BUTTON} gap-1`}
                                      aria-label={`Copy ${label} Boolean string`}
                                    >
                                      <Copy
                                        size={
                                          12
                                        }
                                      />
                                      Copy
                                    </Button>

                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() =>
                                        startEdit(
                                          platformValue,
                                          key,
                                          displayValue,
                                        )
                                      }
                                      className={`${ACTION_PILL_BUTTON} gap-1`}
                                    >
                                      <Edit size={12} />
                                      Edit
                                    </Button>

                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() =>
                                        handleOpenPlatform(
                                          platformValue,
                                          platformLabel,
                                          displayValue,
                                        )
                                      }
                                      className={`${ACTION_PILL_BUTTON} gap-1`}
                                      aria-label={`Search ${platformLabel} with ${label} Boolean string`}
                                    >
                                      <ExternalLink
                                        size={
                                          12
                                        }
                                      />
                                      Search
                                    </Button>
                                  </div>
                                )}
                              </div>

                              {isEditing ? (
                                <Textarea
                                  autoFocus
                                  value={
                                    draftValue
                                  }
                                  onChange={(
                                    event,
                                  ) =>
                                    setDraftValue(
                                      event
                                        .target
                                        .value,
                                    )
                                  }
                                  onKeyDown={(
                                    event,
                                  ) => {
                                    if (
                                      event.key ===
                                      "Escape"
                                    ) {
                                      event.preventDefault();
                                      cancelEdit();
                                    }

                                    if (
                                      (event.ctrlKey ||
                                        event.metaKey) &&
                                      event.key ===
                                      "Enter"
                                    ) {
                                      event.preventDefault();

                                      saveEdit(
                                        platformValue,
                                        key,
                                      );
                                    }
                                  }}
                                  rows={4}
                                  className="w-full resize-y rounded-lg border border-border-primary bg-background-secondary px-3 py-2 text-sm font-mono leading-6 text-text-primary outline-none transition-colors focus:border-tone-blue"
                                />
                              ) : (
                                <p className="select-text break-words text-sm font-mono leading-6 text-text-primary">
                                  {
                                    displayValue
                                  }
                                </p>
                              )}
                            </div>
                          );
                        },
                      )}
                    </div>
                  )}

                  {!isProcessing &&
                    !isFailed &&
                    !isReady && (
                      <div className="space-y-3">
                        {/* An empty state under a killed module must say the tool is
                            unavailable, not offer a Generate button that 503s. The
                            UI has to distinguish "nothing yet, tool available" from
                            "nothing yet, tool unavailable" — in its own words, not
                            by repeating the banner above for each platform. */}
                        <p className="text-sm text-text-muted">
                          {moduleAvailable
                            ? "Not generated yet."
                            : "Not generated yet. Generation is paused while this tool is unavailable."}
                        </p>

                        {moduleAvailable && (
                          <Button
                            type="button"
                            onClick={() =>
                              handleGenerate(
                                platformValue,
                              )
                            }
                            disabled={
                              isSubmitting ||
                              !canGenerate
                            }
                            className={PRIMARY_PILL_BUTTON}
                          >
                            {isSubmitting ? (
                              <Loader2
                                size={
                                  14
                                }
                                className="animate-spin"
                              />
                            ) : null}

                            Generate
                          </Button>
                        )}
                      </div>
                    )}
                </AccordionContent>
              </AccordionItem>
            );
          },
        )}
      </Accordion>
    </div>
  );
};

export default BooleanStringsTab;